import React from 'react';

export interface UnderlineTab<K extends string = string> {
  key: K;
  label: string;
  /** Optional leading icon; inherits the tab's text colour via currentColor. */
  icon?: React.ReactNode;
  /** Optional count chip after the label (rendered for 0 too). */
  count?: number;
}

interface UnderlineTabNavProps<K extends string> {
  tabs: readonly UnderlineTab<K>[];
  active: K;
  onChange: (key: K) => void;
  className?: string;
  /** `lg` is the Outreach inbox's 16px tab (Figma 505:49947). */
  size?: 'default' | 'lg';
}

// lg = Figma 505:49947; its #79736d inactive text is darkened to the AA token --foreground-muted (owner approved).
const SIZE_CLASSES = {
  default: { base: 'text-[15px]', active: 'text-[#171717]', inactive: 'text-[#A3A3A3] hover:text-[#737373]', gap: 'gap-6', badge: 'bg-(--stone-100) px-1.5' },
  lg: { base: 'text-base', active: 'text-black', inactive: 'text-(--foreground-muted) hover:text-foreground', gap: 'gap-8', badge: 'bg-(--stone-200) px-[7px]' },
} as const;

/**
 * Reusable Figma underline tab bar: active tab = dark text + black underline,
 * inactive = muted. Controlled (active / onChange). Icons inherit currentColor
 * so they match the active/inactive text colour.
 */
export function UnderlineTabNav<K extends string>({
  tabs,
  active,
  onChange,
  className = '',
  size = 'default',
}: UnderlineTabNavProps<K>) {
  const look = SIZE_CLASSES[size];
  return (
    <div className={`flex items-center ${look.gap} ${className}`} role="tablist">
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={t.count != null ? `${t.label} ${t.count}` : undefined}
            onClick={() => onChange(t.key)}
            className={`relative flex items-center gap-1.5 pb-2.5 ${look.base} font-medium transition-colors ${
              isActive ? look.active : look.inactive
            }`}
          >
            {t.icon}
            {t.label}
            {t.count != null && (
              <span className={`rounded-full ${look.badge} py-0.5 text-[11px] leading-none font-semibold`}>
                {t.count}
              </span>
            )}
            {/* Selected underline — 2px #000, radius 2px, full tab width. Absolute
                bar (not border-b) so it can be rounded. */}
            {isActive && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-[2px] bg-black" />
            )}
          </button>
        );
      })}
    </div>
  );
}
